import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Package, Upload, X } from "lucide-react";
import { supabase } from "@/supabase";
import { useToast } from "@/hooks/use-toast";
import AdminLayout from "@/components/admin/AdminLayout";
import AuthGuard from "@/components/admin/AuthGuard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { uploadImage } from "@/hooks/uploadImages";

interface Product {
  id: string;
  name: string;
  description: string | null;
  price: number;
  image_url: string | null;
  colors: string[];
  sizes: string[];
  stock_quantity: number;
  stock_status: string;
  is_active: boolean;
  is_on_demand: boolean;
  pagseguro_link: string | null;
}

const AdminProducts = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    image_url: "",
    colors: "",
    sizes: "",
    stock_quantity: "",
    pagseguro_link: "",
    is_active: true,
    is_on_demand: false,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: products, isLoading } = useQuery({
    queryKey: ["admin-products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as Product[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; description?: string | null; price: number; image_url?: string | null; colors?: string[]; sizes?: string[]; stock_quantity?: number; pagseguro_link?: string | null; is_active?: boolean }) => {
      const { error } = await supabase.from("products").insert([data]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      toast({ title: "Produto criado com sucesso!" });
      closeDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Erro ao criar produto", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Omit<Product, 'id' | 'stock_status' | 'created_at' | 'updated_at'>> }) => {
      const { error } = await supabase.from("products").update(data).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      toast({ title: "Produto atualizado com sucesso!" });
      closeDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Erro ao atualizar produto", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("products").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      toast({ title: "Produto excluído com sucesso!" });
    },
    onError: (error: Error) => {
      toast({ title: "Erro ao excluir produto", description: error.message, variant: "destructive" });
    },
  });

  const openDialog = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        description: product.description || "",
        price: product.price.toString(),
        image_url: product.image_url || "",
        colors: product.colors?.join(", ") || "",
        sizes: product.sizes?.join(", ") || "",
        stock_quantity: product.stock_quantity.toString(),
        pagseguro_link: product.pagseguro_link || "",
        is_active: product.is_active,
        is_on_demand: product.is_on_demand || false,
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: "",
        description: "",
        price: "",
        image_url: "",
        colors: "",
        sizes: "",
        stock_quantity: "",
        pagseguro_link: "",
        is_active: true,
        is_on_demand: false,
      });
    }
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingProduct(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const productData = {
      name: formData.name,
      description: formData.description || null,
      price: parseFloat(formData.price),
      image_url: formData.image_url || null,
      colors: formData.colors.split(",").map((c) => c.trim()).filter(Boolean),
      sizes: formData.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      stock_quantity: formData.is_on_demand ? 0 : (parseInt(formData.stock_quantity) || 0),
      pagseguro_link: formData.pagseguro_link || null,
      is_active: formData.is_active,
      is_on_demand: formData.is_on_demand,
    };

    if (editingProduct) {
      updateMutation.mutate({ id: editingProduct.id, data: productData });
    } else {
      createMutation.mutate(productData);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Erro",
        description: "Por favor, selecione apenas arquivos de imagem.",
        variant: "destructive",
      });
      return;
    }

    // Validar tamanho (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Erro",
        description: "A imagem deve ter no máximo 5MB.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUploading(true);
      const imageUrl = await uploadImage(file);
      setFormData({ ...formData, image_url: imageUrl });
      toast({
        title: "Sucesso",
        description: "Imagem enviada com sucesso!",
      });
    } catch (error) {
      console.error("Erro ao fazer upload:", error);
      toast({
        title: "Erro ao fazer upload",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, image_url: "" });
  };

  const stockLabels: Record<string, { label: string; class: string }> = {
    available: { label: "Em estoque", class: "stock-available" },
    low: { label: "Baixo estoque", class: "stock-low" },
    out_of_stock: { label: "Esgotado", class: "stock-out" },
  };

  return (
    <AuthGuard>
      <AdminLayout>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-semibold text-foreground">
              Produtos
            </h1>
            <p className="text-muted-foreground mt-1">
              Gerencie seu catálogo de produtos
            </p>
          </div>
          <button onClick={() => openDialog()} className="btn-primary flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Novo Produto
          </button>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground">Carregando...</div>
        ) : products && products.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <div key={product.id} className="admin-card">
                <div className="aspect-product bg-muted rounded-lg overflow-hidden mb-4">
                  {product.image_url ? (
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-12 h-12 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <h3 className="font-semibold text-foreground mb-1">{product.name}</h3>
                <p className="text-lg font-bold text-primary mb-2">
                  R$ {product.price.toFixed(2).replace(".", ",")}
                </p>
                <div className="flex items-center justify-between mb-4">
                  {product.is_on_demand ? (
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                      Sob encomenda
                    </span>
                  ) : (
                    <span className={stockLabels[product.stock_status]?.class || ""}>
                      {stockLabels[product.stock_status]?.label} ({product.stock_quantity})
                    </span>
                  )}
                  {!product.is_active && (
                    <span className="text-xs text-muted-foreground">Inativo</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openDialog(product)}
                    className="flex-1 flex items-center justify-center gap-2 py-2 border border-border rounded-lg hover:bg-muted transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                    Editar
                  </button>
                  <button
                    onClick={() => {
                      if (confirm("Tem certeza que deseja excluir este produto?")) {
                        deleteMutation.mutate(product.id);
                      }
                    }}
                    className="px-4 py-2 border border-destructive text-destructive rounded-lg hover:bg-destructive/10 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="admin-card text-center py-12">
            <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">Nenhum produto cadastrado ainda.</p>
            <button onClick={() => openDialog()} className="btn-primary">
              Adicionar primeiro produto
            </button>
          </div>
        )}

        {/* Product Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">
                {editingProduct ? "Editar Produto" : "Novo Produto"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="admin-label">Nome </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="admin-input"
                  // required
                />
              </div>
              <div>
                <label className="admin-label">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="admin-input min-h-[80px]"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="admin-label">Preço (R$) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="admin-input"
                    required
                  />
                </div>
                <div>
                  <label className="admin-label">
                    Estoque {!formData.is_on_demand && "*"}
                  </label>
                  <input
                    type="number"
                    value={formData.stock_quantity}
                    onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                    className="admin-input"
                    required={!formData.is_on_demand}
                    disabled={formData.is_on_demand}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                <input
                  type="checkbox"
                  id="is_on_demand"
                  checked={formData.is_on_demand}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    is_on_demand: e.target.checked,
                    stock_quantity: e.target.checked ? "0" : formData.stock_quantity
                  })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_on_demand" className="text-sm text-foreground font-medium">
                  Produto sob encomenda (sem estoque fixo)
                </label>
              </div>
              <div>
                <label className="admin-label">URL da Imagem</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="admin-input"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="admin-label">Ou faça upload de uma imagem</label>
                <div className="space-y-3">
                  {formData.image_url && (
                    <div className="relative w-full h-48 bg-muted rounded-lg overflow-hidden">
                      <img
                        src={formData.image_url}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute top-2 right-2 p-1 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      id="image-upload"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={isUploading}
                      className="hidden"
                    />
                    <label
                      htmlFor="image-upload"
                      className={`flex items-center justify-center gap-2 px-4 py-2 border border-border rounded-lg cursor-pointer hover:bg-muted transition-colors ${
                        isUploading ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <Upload className="w-4 h-4" />
                      {isUploading ? "Enviando..." : "Escolher imagem"}
                    </label>
                    {formData.image_url && (
                      <span className="text-xs text-muted-foreground">
                        Imagem carregada
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Tamanho máximo: 5MB. Formatos: JPG, PNG, GIF, WebP
                  </p>
                </div>
              </div>
              <div>
                <label className="admin-label">Cores (separadas por vírgula)</label>
                <input
                  type="text"
                  value={formData.colors}
                  onChange={(e) => setFormData({ ...formData, colors: e.target.value })}
                  className="admin-input"
                  placeholder="Terracotta, Creme, Areia"
                />
              </div>
              <div>
                <label className="admin-label">Tamanhos (separados por vírgula)</label>
                <input
                  type="text"
                  value={formData.sizes}
                  onChange={(e) => setFormData({ ...formData, sizes: e.target.value })}
                  className="admin-input"
                  placeholder="P, M, G, GG"
                />
              </div>
              <div>
                <label className="admin-label">Link PagSeguro</label>
                <input
                  type="url"
                  value={formData.pagseguro_link}
                  onChange={(e) => setFormData({ ...formData, pagseguro_link: e.target.value })}
                  className="admin-input"
                  placeholder="https://pagseguro.uol.com.br/..."
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_active" className="text-sm text-foreground">
                  Produto ativo (visível na loja)
                </label>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeDialog} className="flex-1 btn-secondary">
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex-1 btn-primary disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "Salvando..."
                    : "Salvar"}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </AdminLayout>
    </AuthGuard>
  );
};

export default AdminProducts;
